"use client";

import { useRouter } from "next/navigation";
import { useEffect, useCallback } from "react";

interface UseSessionTimeoutOptions {
  timeoutDuration?: number;
  redirectPath?: string;
  storageKey?: string;
}

const DEFAULT_TIMEOUT = 60 * 60 * 1000;
const DEFAULT_REDIRECT_PATH = "/";
const DEFAULT_STORAGE_KEY = "withdrawal_session_start";

export const useSessionTimeout = ({
  timeoutDuration = DEFAULT_TIMEOUT,
  redirectPath = DEFAULT_REDIRECT_PATH,
  storageKey = DEFAULT_STORAGE_KEY,
}: UseSessionTimeoutOptions = {}) => {
  const router = useRouter();

  const clearSession = useCallback(() => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(storageKey);
    }
  }, [storageKey]);

  const checkSessionExpiry = useCallback(() => {
    if (typeof window === "undefined") return;

    const sessionStartTime = sessionStorage.getItem(storageKey);

    if (!sessionStartTime) {
      // First visit - set session start time
      sessionStorage.setItem(storageKey, Date.now().toString());
      return;
    }

    const startTime = Number.parseInt(sessionStartTime, 10);
    const currentTime = Date.now();
    const elapsedTime = currentTime - startTime;

    if (elapsedTime >= timeoutDuration) {
      // Session expired - clear storage and redirect
      clearSession();
      router.push(redirectPath);
    }
  }, [storageKey, timeoutDuration, redirectPath, router, clearSession]);

  const resetSession = useCallback(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(storageKey, Date.now().toString());
    }
  }, [storageKey]);

  useEffect(() => {
    // Check session on mount
    checkSessionExpiry();

    // Set up interval to check session expiry every minute
    const intervalId = setInterval(checkSessionExpiry, 60000);

    // Check session on page visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkSessionExpiry();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkSessionExpiry]);

  return {
    clearSession,
    resetSession,
  };
};
